/**
 * Avatars for people who never chose one.
 *
 * Every fallback used to be a request to api.dicebear.com keyed on the user's
 * id. That meant three things nobody wanted: a network round trip per face on
 * a page that might show forty of them, a user id handed to a third party on
 * every render, and -- because the host was not in the frontend's
 * Content-Security-Policy -- a broken image where the person should be.
 *
 * Initials on a colour derived from the name are computed locally, render
 * instantly, work offline, and look deliberate rather than generated. Someone
 * who picks a real avatar still gets it; this only fills the gap.
 */

/**
 * Up to two letters, from the first and last word of a name.
 *
 * Only word characters count. "HR Admin (dev)" used to render as "H(" because
 * the last "word" was a parenthesis -- and names carrying a bracketed suffix or
 * a trailing punctuation mark are common enough (test accounts, imported
 * records, "Jo Smith (maternity cover)") that the fallback avatar showed
 * punctuation to real people.
 */
export function getInitials(name?: string | null): string {
  const words = String(name || '')
    // A parenthesised suffix qualifies the name, it is not part of it:
    // "Jo Smith (maternity cover)" is Jo Smith. Dropping it first keeps the
    // initials on the actual name rather than on the qualifier.
    .replace(/\([^)]*\)/g, ' ')
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * A muted, readable palette. Deliberately desaturated: a wall of avatars is
 * chrome around the content, and saturated circles fight the page for
 * attention. White text passes contrast on every one of these.
 */
const AVATAR_COLORS = [
  '#5B6B8C', '#6B5B8C', '#8C5B6B', '#5B8C7A', '#8C7A5B',
  '#4F7A8C', '#7A5B8C', '#8C6B5B', '#5B8C5F', '#8C5B7A',
];

/** Stable across renders and machines, so a person keeps the same colour. */
export function avatarColor(seed?: string | null): string {
  const key = String(seed || '');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export interface AvatarLike {
  src?: string;
  children?: string;
  sx: { bgcolor?: string; color?: string; fontWeight?: number };
}

/**
 * Props for an MUI `<Avatar>`: the chosen image when there is one, initials on
 * a stable colour when there is not.
 */
export function avatarProps(
  name?: string | null,
  avatarUrl?: string | null,
  seed?: string | null,
): AvatarLike {
  // A picture from the old third-party avatar host is not shown: loading it told that
  // host who was looking (the picker draws in the browser since 2026-10-27).
  if (avatarUrl && !/^https?:\/\/api\.dicebear\.com\//i.test(avatarUrl)) {
    // Still give it initials and a colour: they show while the image loads and
    // remain if it fails, instead of an empty grey disc.
    return {
      src: avatarUrl,
      children: getInitials(name),
      sx: { bgcolor: avatarColor(seed || name), color: '#fff', fontWeight: 500 },
    };
  }
  return {
    children: getInitials(name),
    sx: { bgcolor: avatarColor(seed || name), color: '#fff', fontWeight: 500 },
  };
}
